import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import toast from "react-hot-toast";
import "../styles/global.css";
import "./Login.css";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const githubCallbackProcessed = useRef(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      const returnTo = searchParams.get("returnTo") || "/dashboard";
      navigate(returnTo);
    }
  }, [user, navigate, searchParams]);

  // Initialize Google Sign-In
  useEffect(() => {
    const googleClientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
    if (!googleClientId) return;

    const initializeGoogle = () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
      }
    };

    if (window.google && window.google.accounts) {
      initializeGoogle();
    } else {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.head.appendChild(script);

      return () => {
        const existingScript = document.querySelector(
          'script[src="https://accounts.google.com/gsi/client"]'
        );
        if (existingScript) {
          existingScript.remove();
        }
      };
    }
  }, []);

  // Handle SSO token from WHMCS
  useEffect(() => {
    const ssoToken = searchParams.get("sso_token");
    if (ssoToken && !loading && !user) {
      setLoading(true);
      // Store token and verify it
      localStorage.setItem("token", ssoToken);
      api.defaults.headers.common["Authorization"] = `Bearer ${ssoToken}`;

      // Clean up URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("sso_token");
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Verify token and login
      login("")
        .then((result) => {
          if (result.success) {
            toast.success("Login successful!");
            const returnTo = searchParams.get("returnTo") || "/dashboard";
            navigate(returnTo);
          } else {
            toast.error("Invalid or expired login token");
            localStorage.removeItem("token");
            delete api.defaults.headers.common["Authorization"];
          }
        })
        .catch((error) => {
          console.error("SSO login error:", error);
          toast.error("Login failed. Please try again.");
          localStorage.removeItem("token");
          delete api.defaults.headers.common["Authorization"];
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle GitHub callback
  useEffect(() => {
    const code = searchParams.get("code");
    const stateParam = searchParams.get("state");

    if (code && !githubLoading && !githubCallbackProcessed.current) {
      // Store code and state before cleaning URL
      const codeToProcess = code;
      const stateToProcess = stateParam;

      // Clean up URL immediately to prevent duplicate requests
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("code");
      newSearchParams.delete("state");
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Process the callback with stored values (it will set the flag internally)
      handleGitHubCallback(codeToProcess, stateToProcess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter your email and password");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/whmcs-auth/login", {
        email: email.trim(),
        password: password,
      });

      if (response.data.success) {
        // Store token
        localStorage.setItem("token", response.data.token);
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.token}`;

        // Update auth context
        await login("");

        toast.success("Login successful!");
        const returnTo = searchParams.get("returnTo") || "/dashboard";
        navigate(returnTo);
      } else {
        toast.error(response.data.error || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(
        error.response?.data?.error || "Login failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (response) => {
    if (!response || !response.credential) {
      toast.error("Google Sign-In failed. Please try again.");
      return;
    }

    setGoogleLoading(true);
    try {
      const apiResponse = await api.post("/whmcs-auth/google", {
        id_token: response.credential,
      });

      if (apiResponse.data.success) {
        localStorage.setItem("token", apiResponse.data.token);
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${apiResponse.data.token}`;

        await login("");

        toast.success(apiResponse.data.message || "Logged in with Google!");
        const returnTo = searchParams.get("returnTo") || "/dashboard";
        navigate(returnTo);
      } else {
        toast.error(apiResponse.data.error || "Google Sign-In failed");
      }
    } catch (error) {
      console.error("Google OAuth error:", error);
      toast.error(
        error.response?.data?.error ||
          "Google Sign-In failed. Please try again."
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleClick = (e) => {
    e.preventDefault();
    if (!window.google || !window.google.accounts) {
      toast.error("Google Sign-In is not available");
      return;
    }

    // Trigger Google Sign-In popup
    const temp = document.createElement("div");
    temp.style.position = "fixed";
    temp.style.top = "-9999px";
    document.body.appendChild(temp);
    window.google.accounts.id.renderButton(temp, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
    });
    setTimeout(() => {
      const btn = temp.querySelector("div[role='button']");
      if (btn) btn.click();
      setTimeout(() => temp.remove(), 800);
    }, 80);
  };

  const handleGitHubClick = (e) => {
    e.preventDefault();
    const githubClientId = process.env.REACT_APP_GITHUB_OAUTH_CLIENT_ID;
    if (!githubClientId) {
      toast.error("GitHub Sign-In is not configured");
      return;
    }

    // Use /login as the single callback URL (GitHub only allows one callback URL)
    const redirectUri = `${window.location.origin}/login`;
    const state = {
      returnTo: searchParams.get("returnTo") || "/dashboard",
      from: "login", // Track that user came from login page
      ts: Date.now(),
    };

    const authUrl = `https://github.com/login/oauth/authorize?client_id=${encodeURIComponent(
      githubClientId
    )}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&scope=user:email&state=${encodeURIComponent(JSON.stringify(state))}`;

    window.location.href = authUrl;
  };

  const handleGitHubCallback = async (code, stateParam = null) => {
    // Prevent duplicate calls
    if (githubLoading || githubCallbackProcessed.current) {
      console.log("GitHub callback already processed, skipping...");
      return;
    }

    // Mark as processed immediately to prevent duplicate calls
    githubCallbackProcessed.current = true;
    setGithubLoading(true);
    try {
      // Use /login as the single callback URL (GitHub only allows one callback URL)
      // CRITICAL: This must exactly match the redirect_uri used in the authorization request
      const redirectUri = `${window.location.origin}/login`;

      // Use stateParam passed as parameter, or try to get from URL as fallback
      const state = stateParam || searchParams.get("state");

      console.log("GitHub OAuth callback:", {
        code: code ? `${code.substring(0, 10)}...` : "missing",
        redirectUri: redirectUri,
        origin: window.location.origin,
        hasState: !!state,
        timestamp: new Date().toISOString(),
      });

      const response = await api.post("/whmcs-auth/github", {
        code: code,
        redirect_uri: redirectUri, // Must match exactly what was sent to GitHub
      });

      if (response.data.success) {
        localStorage.setItem("token", response.data.token);
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.token}`;

        await login("");

        // Determine message and redirect based on returnTo in state
        let message = response.data.message || "Logged in with GitHub!";
        let returnTo = "/dashboard";

        if (state) {
          try {
            const parsedState = JSON.parse(decodeURIComponent(state));
            returnTo = parsedState.returnTo || "/dashboard";
          } catch (e) {
            // Ignore parse errors
          }
        }

        toast.success(message);
        navigate(returnTo);
      } else {
        toast.error(response.data.error || "GitHub Sign-In failed");
      }
    } catch (error) {
      console.error("GitHub OAuth error:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      const errorMessage =
        error.response?.data?.error ||
        "GitHub Sign-In failed. Please try again.";
      toast.error(errorMessage);

      // Don't redirect on error - let user stay on login page to try again
    } finally {
      setGithubLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  const githubClientId = process.env.REACT_APP_GITHUB_OAUTH_CLIENT_ID;

  return (
    <div className="login-page">
      <div className="login-logo">
        <img
          src="/logo.png"
          alt="Logo"
          onError={(e) => (e.target.style.display = "none")}
        />
      </div>

      <div className="login-container">
        <div className="auth-wrapper">
          <div className="auth-card">
            <div className="auth-title">Log in</div>
            <div className="auth-subtitle">Access your dashboard instantly</div>
            <div className="social-stack">
              <button
                id="google-login-btn"
                className="btn btn-secondary"
                type="button"
                onClick={handleGoogleClick}
                disabled={
                  googleLoading || loading || githubLoading || !googleClientId
                }
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}
                >
                  <path
                    d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.042l2.007-2.332z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
                    fill="#EA4335"
                  />
                </svg>
                {googleLoading ? "Signing in..." : "Continue with Google"}
              </button>

              <button
                id="github-login-btn"
                className="btn btn-secondary"
                type="button"
                onClick={handleGitHubClick}
                disabled={
                  googleLoading || loading || githubLoading || !githubClientId
                }
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ marginRight: "8px" }}
                >
                  <path
                    fillRule="evenodd"
                    d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                    clipRule="evenodd"
                  />
                </svg>
                {githubLoading ? "Signing in..." : "Continue with GitHub"}
              </button>
            </div>

            <div className="divider">or</div>

            <form onSubmit={handleEmailLogin}>
              <div className="input-group">
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="login-password">Password</label>
                <a
                  href="https://cyberin.in/manage/password/reset"
                  className="forgot-password"
                >
                  Forgot?
                </a>
                <input
                  id="login-password"
                  type="password"
                  placeholder="Your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleEmailLogin(e);
                    }
                  }}
                  required
                />
              </div>
              <button
                id="login-submit-btn"
                className="btn btn-primary"
                type="submit"
                disabled={loading || googleLoading || githubLoading}
              >
                {loading ? "Logging in..." : "Log in"}
              </button>
              <div className="mt-2 text-center">
                <a
                  href="https://cyberin.in/order/signup?returnTo=cloud"
                  className="create-account-link"
                >
                  New here? Create an account
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
